import { CheckOutlined } from "@ant-design/icons";
import { Button, Empty, Space, Switch, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMarkNotificationRead, useNotificationsList } from "../hooks/useNotifications";
import { type NotificationItem, notificationTypeLabel } from "../types";

const { Title, Text } = Typography;

/**
 * Foydalanuvchining O'Z bildirishnomalari.
 *
 * ⚠️ Marshrutda `staticData.requirePermission` YO'Q — dashboard bilan bir
 * xil holat: bu foydalanuvchining o'z ma'lumoti va backend uni
 * `recipient_id` bo'yicha filtrlaydi (KONTRAKT NUQSONI N7). Ruxsat talab
 * qilinsa, roli yo'q yangi xodim o'z xabarlarini ko'ra olmasdi.
 *
 * ⚠️ Reja `antd` ning `List` komponentini yozgan edi — **antd 6 da u
 * DEPRECATED** («will be removed in next major version», konsolga
 * ogohlantirish chiqaradi) va loyihaning boshqa hech bir sahifasi uni
 * ishlatmaydi. Shu sababli mavjud `Table` naqshi qo'llandi.
 */
export function NotificationsPage() {
	const { t } = useTranslation("notifications");
	const [unread, setUnread] = useState(false);
	const query = useNotificationsList(unread);
	const markRead = useMarkNotificationRead();

	const columns: ColumnsType<NotificationItem> = [
		{
			title: t("table.content"),
			key: "content",
			render: (_, item) => (
				<Space orientation="vertical" size={0}>
					<span className={item.readAt ? "text-gray-500" : "font-semibold"}>
						{item.title}{" "}
						<Tag color={item.readAt ? "default" : "blue"}>
							{t(`types.${item.type}`, { defaultValue: notificationTypeLabel(item.type) })}
						</Tag>
					</span>
					<span className={item.readAt ? "text-gray-400" : ""}>{item.body}</span>
				</Space>
			),
		},
		{
			title: t("table.createdAt"),
			dataIndex: "createdAt",
			key: "createdAt",
			width: 200,
			// ⚠️ Server `createdAt` ni ISO (UTC) qaytaradi; ko'rsatish brauzer
			// zonasida. Sozlamadagi `timezone` bu yerda O'QILMAYDI — u domen
			// hisobiga tegishli, ko'rsatishga emas.
			render: (createdAt: string) => (
				<span className="text-gray-400">{new Date(createdAt).toLocaleString("uz-UZ")}</span>
			),
		},
		{
			title: "",
			key: "actions",
			align: "right",
			width: 140,
			render: (_, item) =>
				item.readAt ? null : (
					<Button
						type="link"
						icon={<CheckOutlined />}
						// ⚠️ Faqat AYNAN shu qator kutadi — `isPending` yolg'iz
						// bo'lsa bitta bosishda butun ro'yxat spinnerga tushardi
						// (`SettingsPage` dagi bir xil naqsh).
						loading={markRead.isPending && markRead.variables === item.id}
						onClick={() => markRead.mutate(item.id)}
					>
						{t("actions.markRead")}
					</Button>
				),
		},
	];

	return (
		<div>
			<div className="mb-4 flex items-center justify-between">
				<Title level={3} className="!mb-0">
					{t("page.title")}
				</Title>
				<div className="flex items-center gap-2">
					<Text>{t("page.unreadOnly")}</Text>
					<Switch
						checked={unread}
						onChange={(checked) => {
							// Filtr almashganda birinchi sahifaga qaytamiz — 3-sahifada
							// turib filtrlash bo'sh ro'yxat ko'rsatardi.
							query.setPage(1);
							setUnread(checked);
						}}
					/>
				</div>
			</div>

			<Table<NotificationItem>
				columns={columns}
				dataSource={query.items}
				rowKey="id"
				loading={query.isLoading}
				locale={{ emptyText: <Empty description={t("table.empty")} /> }}
				pagination={{
					current: query.meta.page,
					pageSize: query.meta.limit,
					total: query.meta.total,
					onChange: (page, pageSize) => {
						query.setPage(page);

						if (pageSize !== query.limit) {
							query.setLimit(pageSize);
						}
					},
				}}
				scroll={{ x: "max-content" }}
			/>
		</div>
	);
}
